import { useMemo } from 'react';
import { omitBy, isNil } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import { ResolvableModeEnum } from '@e-socna/data-layer';
import { EMPTY_OBJECT } from '@e-socna/empty';
import type { ServiceUsageOrigin } from '@e-socna/data-layer';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import type { ResolvableServiceUsageForm_ResolvableServiceUsage } from '../fragments/resolvable-service-usage.fragment';

interface Params {
  readonly index: number;
  readonly initialValues?: ResolvableServiceUsageForm_ResolvableServiceUsage | null;
  readonly serviceUsageOrigin?: ServiceUsageOrigin;
}

export const useInitialValuesFactory = ({
  index,
  initialValues: pInitialValues,
  serviceUsageOrigin,
}: Readonly<Params>) => {
  const { generateId } = useIdFactory();

  const referencesContext = useReferencesContext();

  const { defaultUnit } = ensureDefined({
    defaultUnit: referencesContext.defaultUnit,
  });

  const { contentOriginsCodeMap } = referencesContext;

  const initialValues: ResolvableServiceUsageForm_ResolvableServiceUsage =
    useMemo(
      () => ({
        __typename: 'ResolvableServiceUsage',
        _id: generateId(),
        index,
        contentOriginId: serviceUsageOrigin
          ? contentOriginsCodeMap.Catalog._id
          : contentOriginsCodeMap.Special._id,
        serviceUsageOrigin,
        ...(!serviceUsageOrigin
          ? {
              serviceOriginId: contentOriginsCodeMap.Catalog._id,
            }
          : EMPTY_OBJECT),
        ...omitBy(pInitialValues, isNil),
        ...(!serviceUsageOrigin
          ? {
              settings: {
                __typename: 'ResolvableServiceUsageSettings',
                showAmountsInQuotation: {
                  __typename: 'ResolvableBoolean',
                  mode: ResolvableModeEnum.Static,
                  value: false,
                },
                showCostsInQuotation: {
                  __typename: 'ResolvableBoolean',
                  mode: ResolvableModeEnum.Static,
                  value: false,
                },
                showMaterialsInQuotation: {
                  __typename: 'ResolvableBoolean',
                  mode: ResolvableModeEnum.Static,
                  value: false,
                },
                showQuantitiesInQuotation: {
                  __typename: 'ResolvableBoolean',
                  mode: ResolvableModeEnum.Static,
                  value: false,
                },
                ...omitBy(pInitialValues?.settings, isNil),
              },
              quantity: {
                __typename: 'ResolvableQuantity',
                value: {
                  __typename: 'ResolvableFloat',
                  mode: ResolvableModeEnum.Static,
                  value: 1,
                },
                unitId: {
                  __typename: 'ResolvableId',
                  mode: ResolvableModeEnum.Static,
                  value: defaultUnit._id,
                },
                ...omitBy(pInitialValues?.quantity, isNil),
              },
            }
          : EMPTY_OBJECT),
      }),
      [
        contentOriginsCodeMap.Catalog._id,
        contentOriginsCodeMap.Special._id,
        defaultUnit._id,
        generateId,
        index,
        pInitialValues,
        serviceUsageOrigin,
      ],
    );

  return { initialValues };
};
